// /app/frontend/src/components/frugal-v2/tabs/AccountsTab.jsx
// Accounts management tab - Banks and Cards (< 200 lines)

import React, { useState, useEffect, useCallback } from 'react';
import { Building2, CreditCard } from 'lucide-react';
import * as api from '../services/frugalApi';
import AccountRow from '../components/AccountRow';
import ReceivablePayableSection from '../components/ReceivablePayableSection';

const AccountsTab = ({ token, t }) => {
  const [accounts, setAccounts] = useState({ banks: [], cards: [], receivables: [], payables: [] });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', balance: '' });
  const [expandedId, setExpandedId] = useState(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const data = await api.fetchAccounts(token);
      setAccounts(data);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleCreate = async (type, index) => {
    const name = type === 'bank' 
      ? `${t('frugal.accounts.banks')} ${index}`
      : `${t('frugal.accounts.cards')} ${index}`;
    try {
      await api.createAccount(token, { name, balance: 0, account_type: type, is_default: false });
      fetchAccounts();
    } catch (err) {
      console.error('Error creating account:', err);
    }
  };

  const handleUpdate = async (id) => {
    try {
      await api.updateAccount(token, id, {
        name: editForm.name,
        balance: parseFloat(editForm.balance) || 0
      });
      setEditingId(null);
      fetchAccounts();
    } catch (err) {
      console.error('Error updating account:', err);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await api.setDefaultAccount(token, id);
      fetchAccounts();
    } catch (err) {
      console.error('Error setting default:', err);
    }
  };

  const startEdit = (account) => {
    setEditingId(account.id);
    const rounded = account.balance != null ? Number(account.balance).toFixed(2) : '0.00';
    setEditForm({ name: account.name, balance: rounded });
  };

  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);
  const handleEditFormChange = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }));

  const bankSlots = [accounts.banks[0] || null, accounts.banks[1] || null, accounts.banks[2] || null, accounts.banks[3] || null];
  const cardSlots = [accounts.cards[0] || null, accounts.cards[1] || null, accounts.cards[2] || null, accounts.cards[3] || null];

  const totalBanks = accounts.banks.reduce((sum, a) => sum + (a.balance || 0), 0);
  const totalCards = accounts.cards.reduce((sum, a) => sum + (a.balance || 0), 0);
  const netPosition = totalBanks - totalCards;
  const totalReceivables = (accounts.receivables || []).reduce((s, a) => s + (a.balance || 0), 0);
  const totalPayables = (accounts.payables || []).reduce((s, a) => s + (a.balance || 0), 0);
  const totalBalance = netPosition + totalReceivables - totalPayables;

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Banks Section */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-emerald-400" />
          <h3 className="text-white font-medium">{t('frugal.accounts.banks')}</h3>
          <span className="ml-auto text-emerald-400 font-medium">
            ${totalBanks.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="space-y-2">
          {bankSlots.map((account, idx) => (
            <AccountRow key={account?.id || `bank-${idx}`} account={account} index={idx + 1} type="bank"
              colorClass="bg-emerald-500" t={t} token={token} isExpanded={expandedId === account?.id}
              isEditing={editingId === account?.id} editForm={editForm} onToggleExpand={toggleExpand}
              onStartEdit={startEdit} onSetDefault={handleSetDefault} onEditFormChange={handleEditFormChange}
              onUpdate={handleUpdate} onCancelEdit={() => setEditingId(null)} onCreate={() => handleCreate('bank', idx + 1)} />
          ))}
        </div>
      </div>

      {/* Cards Section */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-amber-400" />
          <h3 className="text-white font-medium">{t('frugal.accounts.cards')}</h3>
          <span className="ml-auto text-amber-400 font-medium">
            ${totalCards.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="space-y-2">
          {cardSlots.map((account, idx) => (
            <AccountRow key={account?.id || `card-${idx}`} account={account} index={idx + 1} type="card"
              colorClass="bg-amber-500" t={t} token={token} isExpanded={expandedId === account?.id}
              isEditing={editingId === account?.id} editForm={editForm} onToggleExpand={toggleExpand}
              onStartEdit={startEdit} onSetDefault={handleSetDefault} onEditFormChange={handleEditFormChange}
              onUpdate={handleUpdate} onCancelEdit={() => setEditingId(null)} onCreate={() => handleCreate('card', idx + 1)} />
          ))}
        </div>
      </div>

      {/* Net Position */}
      <div className="bg-gradient-to-r from-emerald-500/20 to-amber-500/20 rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between">
          <span className="text-slate-400">{t('frugal.accounts.netBalance')}</span>
          <span className={`text-xl font-bold ${netPosition >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            ${netPosition.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      {/* Receivables Section */}
      <ReceivablePayableSection
        token={token}
        t={t}
        type="receivable"
        items={accounts.receivables || []}
        onChange={fetchAccounts}
      />

      {/* Payables Section */}
      <ReceivablePayableSection
        token={token}
        t={t}
        type="payable"
        items={accounts.payables || []}
        onChange={fetchAccounts}
      />

      {/* Total Balance = Net + Receivables - Payables */}
      <div className="bg-gradient-to-r from-purple-500/20 to-sky-500/20 rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between">
          <span className="text-white font-semibold">
            {t('frugal.accounts.totalBalance', 'Total Balance')}
          </span>
          <span className={`text-2xl font-bold ${totalBalance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
            data-testid="accounts-total-balance">
            ${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="text-[11px] text-slate-500 text-right mt-1">
          Net {netPosition >= 0 ? '+' : '−'} ${Math.abs(netPosition).toFixed(2)} · Receivable +${totalReceivables.toFixed(2)} · Payable −${totalPayables.toFixed(2)}
        </div>
      </div>
    </div>
  );
};

export default AccountsTab;
